// Match.js //oldVersion_removedenvver
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from '../AuthContext';
import axiosInstance, { apiHelpers } from './axiosInstance';
import callService from "../Services/callService";
import agoraService from "../Services/agoraService";
import { TutorialProvider } from '../components/TutorialManager';
import BunnyHelper from '../components/BunnyHelper';
import _ from 'lodash';
import { 
  Mic, 
  MicOff, 
  Phone, 
  User, 
  X as XIcon, 
  Edit, 
  Save, 
  Volume2, 
  Circle, 
  Headphones, 
  AlertTriangle, 
  Star,
  Coffee
} from 'lucide-react';

// Constants
const SOCKET_CONNECTION_TIMEOUT = 10000;
const MATCH_TIMEOUT = 30000;
const HEARTBEAT_INTERVAL = 5000;
const RETRY_DELAY = 2000;
const AUDIO_CHECK_INTERVAL = 2000;
const QUALITY_CHECK_INTERVAL = 5000;

// Base style classes
const baseClasses = {
  container: "min-h-screen bg-gray-50 py-4 px-4 sm:px-6 lg:px-8",
  card: "bg-white rounded-lg shadow-sm p-4 mb-4",
  heading: "text-gray-900 font-bold",
  subheading: "text-gray-600 font-medium",
  input: `w-full p-3 border rounded-lg focus:ring-2 focus:ring-teal-500/20 
          focus:border-teal-500 bg-white text-gray-900 border-gray-200`,
  textarea: `w-full p-3 border rounded-lg focus:ring-2 focus:ring-teal-500/20 
             focus:border-teal-500 bg-white text-gray-900 border-gray-200`,
  label: "block text-sm font-medium text-gray-700 mb-1.5",
  text: "text-gray-700",
  button: {
    primary: `px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 
              disabled:opacity-50 disabled:cursor-not-allowed`,
    secondary: `px-4 py-2 text-gray-700 hover:text-gray-900`,
    danger: `px-4 py-2 bg-rose-500 text-white rounded-lg hover:bg-rose-600 
             disabled:opacity-50 disabled:cursor-not-allowed`,
    icon: `p-2 rounded-full hover:bg-gray-100`
  },
  status: {
    good: "text-teal-500",
    warning: "text-amber-500",
    error: "text-rose-500"
  }
};

function Match() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Connection and Socket State
  const [isOnMatchScreen, setIsOnMatchScreen] = useState(false);
  const [isAvailableForMatch, setIsAvailableForMatch] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  const [socketHealth, setSocketHealth] = useState({ connected: false, lastPing: null });

  // Socket and connection references
  const socketInitialized = useRef(false);
  const socketRetryCount = useRef(0);
  const maxRetryAttempts = 3;
  const retryTimeout = useRef(null);
  const [roleCounts, setRoleCounts] = useState({ talker: 0, listener: 0 });

  // Audio State
  const [isMuted, setIsMuted] = useState(false);
  const [isTalker, setIsTalker] = useState(null);
  const [audioDevices, setAudioDevices] = useState([]);
  const [selectedAudioDevice, setSelectedAudioDevice] = useState('');
  const [audioStatus, setAudioStatus] = useState({ 
    local: false, 
    remote: false,
    volume: 0,
    quality: 'unknown'
  });
  const [audioPermissionGranted, setAudioPermissionGranted] = useState(false);

  // UI State
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isEditingInfo, setIsEditingInfo] = useState(false);
  const [isShowingAudioSettings, setIsShowingAudioSettings] = useState(false);
  const [diagnosticInfo, setDiagnosticInfo] = useState(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [isEditingInterests, setIsEditingInterests] = useState(false);
  const [isEditingMood, setIsEditingMood] = useState(false);
  const [languages, setLanguages] = useState([]);
  const [isEditingLanguages, setIsEditingLanguages] = useState(false);

  // User Info State
  const [username, setUsername] = useState("Loading...");
  const [rating, setRating] = useState("Loading...");
  const [interests, setInterests] = useState("");
  const [mood, setMood] = useState("");
  const [isUpdatingInfo, setIsUpdatingInfo] = useState(false);
  const [userStatus, setUserStatus] = useState({
    online: false,
    lastActive: null,
    currentRole: null
  });

  // Call State
  const [match, setMatch] = useState(null);
  const [callStatus, setCallStatus] = useState('disconnected');
  const [callDuration, setCallDuration] = useState(0);
  const [showRating, setShowRating] = useState(false);
  const [currentRating, setCurrentRating] = useState(0);
  const [isSubmittingRating, setIsSubmittingRating] = useState(false);
  const [error, setError] = useState(null);
  const [isProcessingMatch, setIsProcessingMatch] = useState(false);
  const [callQuality, setCallQuality] = useState({
    latency: 0,
    packetLoss: 0,
    status: 'good'
  });

  const BLOCKED_WORDS = [
    'horny', 'sex', 'xxx', 'orgasm', 'porn', 'nsfw', 'adult',
    'fuck', 'ass', 'dick', 'pussy', 'nude', 'naked', 'sexual',
  ].map(word => word.toLowerCase());

  // Refs
  const currentMatchRef = useRef(null);
  const userInfoIntervalRef = useRef(null);
  const audioMonitoringRef = useRef(null);
  const socketConnectionTimeout = useRef(null);
  const matchTimeout = useRef(null);
  const heartbeatInterval = useRef(null);
  const callDurationInterval = useRef(null);
  const qualityCheckInterval = useRef(null);
  const audioContextRef = useRef(null);


  const handleInterestsChange = useCallback(
    _.debounce(async (value) => {
      try {
        await axiosInstance.put('/api/auth/user/update-info', {
          interests: value.trim(),
          timestamp: Date.now()
        });
      } catch (error) {
        console.error('Error updating interests:', error);
      }
    }, 500),
    []
  );

  const handleMoodChange = useCallback(
    _.debounce(async (value) => {
      try {
        await axiosInstance.put('/api/auth/user/update-info', {
          mood: value.trim(),
          timestamp: Date.now()
        });
      } catch (error) {
        console.error('Error updating mood:', error);
      }
    }, 500),
    []
  );


  // Base Utility Functions (No dependencies)
  const checkAndRequestAudioPermission = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(track => track.stop());
      setAudioPermissionGranted(true);
      return true;
    } catch (error) {
      console.error('Audio permission error:', error);
      setError('Microphone access is required');
      setAudioPermissionGranted(false);
      return false;
    }
  };

  const setupAudioDevices = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = devices.filter(d => d.kind === 'audioinput');
      setAudioDevices(audioInputs);

      if (audioInputs.length > 0 && !selectedAudioDevice) {
        setSelectedAudioDevice(audioInputs[0].deviceId);
      }
    } catch (error) {
      console.error('Error getting audio devices:', error);
      setError('Failed to access audio devices');
    }
  };

  const syncUserState = async () => {
    try {
      const response = await axiosInstance.get('/api/auth/user/active-status');
      const { isOnline, isAvailable } = response.data;
      
      if (!isOnline) {
        await apiHelpers.setUserActive({
          deviceInfo: {
            type: /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
            browser: navigator.userAgent
          }
        });
      }
    } catch (error) {
      console.error('Failed to sync user state:', error);
    }
  };

  const fetchUserInfo = async () => {
    try {
      if (isEditingInfo) return;
  
      const response = await axiosInstance.get('/api/auth/user/info');
      if (response.data) {
        setUsername(response.data.username);
        if (!isEditingInfo) {
          setInterests(response.data.interests || '');
          setMood(response.data.mood || '');
          setLanguages(response.data.languages || []);
        }
        const displayRating = response.data.stars !== undefined
          ? `${response.data.stars.toFixed(1)} (${response.data.ratingCount} ratings)`
          : "No ratings yet";
        setRating(displayRating);
        setError(null);
      }
    } catch (error) {
      console.error("Error fetching user info:", error);
      setError("Failed to load user information");
    }
  };

  const handleUpdateUserInfo = async () => {
    if (isUpdatingInfo) return;
    
    setIsUpdatingInfo(true);
    try {
      const response = await axiosInstance.put('/api/auth/user/update-info', {
        interests: interests.trim(),
        mood: mood.trim(),
        timestamp: Date.now()
      });
  
      if (response.data.user) {
        setInterests(response.data.user.interests || '');
        setMood(response.data.user.mood || '');
        setIsEditingInfo(false);
        setError(null);
      }
    } catch (error) {
      console.error('Error updating user info:', error);
      setError('Failed to update user information');
    } finally {
      setIsUpdatingInfo(false);
    }
  };

  // Event Basics (No major dependencies)
  const handleRemoteUserEvent = useCallback((event) => {
    if (event.type === 'mute') {
      setMatch(prev => prev ? { ...prev, isMuted: true } : prev);
    } else if (event.type === 'unmute') {
      setMatch(prev => prev ? { ...prev, isMuted: false } : prev);
    }
  }, []);

  const handleCallQualityUpdate = useCallback((stats) => {
    setCallQuality(stats);
    if (stats.status === 'poor' && !error) {
      setError('Poor connection quality. Check your internet connection.');
    }
  }, [error]);

  

  // Core Monitoring Functions
  const startCallDurationTracking = useCallback((status = callStatus) => {
    // Log initial state when function is called
    console.log('Starting call duration tracking:', {
        currentDuration: callDuration,
        callStatus: status,        // Using passed status parameter
        currentCallStatus: callStatus,  // Also log current state for comparison
        hasInterval: !!callDurationInterval.current,
        timestamp: new Date().toISOString()
    });

    // Clean up existing interval if any
    if (callDurationInterval.current) {
        console.log('Clearing existing duration interval');
        clearInterval(callDurationInterval.current);
        callDurationInterval.current = null;
    }

    // Use passed status parameter instead of state
    if (status === 'connected') {
        // Calculate start time considering any existing duration
        const startTime = Date.now() - (callDuration * 1000);
        
        // Create new interval
        callDurationInterval.current = setInterval(() => {
            const newDuration = Math.floor((Date.now() - startTime) / 1000);
            
            // Log duration updates
            console.log('Updating call duration:', {
                newDuration,
                timestamp: new Date().toISOString()
            });
            
            setCallDuration(newDuration);
        }, 1000);

        // Log confirmation that tracking started
        console.log('Call duration tracking initialized:', {
            startTime: new Date(startTime).toISOString(),
            hasInterval: !!callDurationInterval.current,
            initialDuration: callDuration,
            timestamp: new Date().toISOString()
        });
    } else {
        console.log('Call duration tracking not started - status not connected:', {
            status,
            currentCallStatus: callStatus,
            timestamp: new Date().toISOString()
        });
    }
}, [callDuration, callStatus]);

const startCallQualityMonitoring = useCallback((status = callStatus) => {
  console.log('Starting call quality monitoring:', {
      currentStatus: status,           // Use passed status
      stateStatus: callStatus,         // Current state for comparison
      hasInterval: !!qualityCheckInterval.current,
      timestamp: new Date().toISOString()
  });

  // Clean up existing interval
  if (qualityCheckInterval.current) {
      console.log('Clearing existing quality monitoring interval');
      clearInterval(qualityCheckInterval.current);
      qualityCheckInterval.current = null;
  }

  // Start new monitoring only if connected
  if (status === 'connected') {
      qualityCheckInterval.current = setInterval(async () => {
          try {
              const stats = await agoraService.getCallStats();
              console.log('Call quality stats received:', {
                  latency: stats.latency,
                  packetLoss: stats.packetLoss,
                  timestamp: new Date().toISOString()
              });

              // Validate stats
              if (stats.latency === undefined || stats.packetLoss === undefined) {
                  console.warn('Invalid call stats received:', stats);
                  return;
              }

              const newQuality = {
                  latency: stats.latency,
                  packetLoss: stats.packetLoss,
                  status: stats.latency < 150 ? 'good' : 
                          stats.latency < 300 ? 'warning' : 'poor',
                  timestamp: Date.now()
              };

              setCallQuality(newQuality);

              // Log quality changes
              if (newQuality.status !== callQuality.status) {
                  console.log('Call quality changed:', {
                      from: callQuality.status,
                      to: newQuality.status,
                      stats: newQuality,
                      timestamp: new Date().toISOString()
                  });
              }

          } catch (error) {
              console.error('Failed to get call stats:', {
                  error,
                  currentStatus: status,
                  timestamp: new Date().toISOString()
              });

              setCallQuality(prev => ({
                  ...prev,
                  status: 'error',
                  lastError: error.message,
                  timestamp: Date.now()
              }));
          }
      }, QUALITY_CHECK_INTERVAL);

      console.log('Quality monitoring started successfully', {
          interval: QUALITY_CHECK_INTERVAL,
          hasInterval: !!qualityCheckInterval.current,
          timestamp: new Date().toISOString()
      });
  } else {
      console.log('Quality monitoring not started - status not connected:', {
          status,
          stateStatus: callStatus,
          timestamp: new Date().toISOString()
      });
  }
}, [callStatus, callQuality.status]);

  const startAudioMonitoring = useCallback(() => {
    audioMonitoringRef.current = setInterval(async () => {
        try {
            const diagnosis = await agoraService.diagnoseMissingAudio();
            console.log('Raw diagnosis:', diagnosis);

            if (diagnosis?.localTrack) {
                const newAudioStatus = {
                    local: diagnosis.localTrack.exists && diagnosis.localTrack.enabled,
                    remote: diagnosis.remoteUsers?.some(u => u?.hasAudio && u?.audioEnabled),
                    volume: diagnosis.localTrack.volume || 0,
                    quality: diagnosis.callInfo?.quality || 'unknown',
                    rawDiagnosis: diagnosis
                };

                console.log('Setting audio status:', newAudioStatus);
                setAudioStatus(newAudioStatus);
            }
        } catch (error) {
            console.error('Audio monitoring error:', error);
        }
    }, 2000);
}, []);

const cleanup = useCallback(async () => {
  const currentRole = isTalker;  // Keep this capture
  const isRating = showRating;   // Keep this capture

  console.log('Starting cleanup process:', {
      role: currentRole ? 'talker' : 'listener',  // Better logging format
      isRating,
      timestamp: new Date().toISOString()
  });

  try {
      // Clear all intervals
      [
          audioMonitoringRef.current,
          userInfoIntervalRef.current,
          heartbeatInterval.current,
          socketConnectionTimeout.current,
          matchTimeout.current,
          retryTimeout.current,
          callDurationInterval.current,
          qualityCheckInterval.current
      ].forEach(interval => {
          if (interval) clearInterval(interval);
      });

      // Reset all interval refs
      audioMonitoringRef.current = null;
      userInfoIntervalRef.current = null;
      heartbeatInterval.current = null;
      socketConnectionTimeout.current = null;
      matchTimeout.current = null;
      retryTimeout.current = null;
      callDurationInterval.current = null;
      qualityCheckInterval.current = null;

      // Reset call-specific states only
      setDiagnosticInfo(null);
      setAudioStatus({
          local: false,
          remote: false,
          volume: 0,
          quality: 'unknown'
      });
      setIsMuted(false);
      setCallDuration(0);
      setCallQuality({
          latency: 0,
          packetLoss: 0,
          status: 'good'
      });

      // Handle user availability but preserve role if rating
      if (isAvailableForMatch) {
          await apiHelpers.setUserUnavailable();
          setIsAvailableForMatch(false);
      }

      // Clean up audio context
      if (audioContextRef.current?.state !== 'closed') {
          await audioContextRef.current?.close();
          audioContextRef.current = null;
      }

      // Clean up call service
      await callService.cleanup();

      // If we're showing rating dialog, preserve the role
      if (!isRating) {
          setMatch(null);
          currentMatchRef.current = null;
      }

      console.log('Cleanup completed:', {
          preservedRole: isRating ? currentRole : null,
          isRating,
          timestamp: new Date().toISOString()
      });
  } catch (error) {
      console.error('Error during cleanup:', error);
      // Don't reset critical states if rating
      if (!isRating) {
          setMatch(null);
          currentMatchRef.current = null;
      }
  }
}, [isAvailableForMatch, showRating, isTalker]);


  // At the top of Match.js, after all your state declarations
  const startSocketHealthCheck = useCallback(() => {
    // Clear existing interval
    if (heartbeatInterval.current) {
        clearInterval(heartbeatInterval.current);
    }

    heartbeatInterval.current = setInterval(async () => {
        try {
            if (isOnMatchScreen) {
                // 1. Get socket reference and check its state
                const socket = window.socket || callService.socket;
                const isSocketConnected = socket?.connected || false;
                
                // 2. Check API status as before
                const status = await apiHelpers.checkActiveStatus();
                const isApiHealthy = status.data && status.data.isOnline;
                
                // 3. Log current states for debugging
                console.log('Health check states:', {
                    socketConnected: isSocketConnected,
                    apiHealthy: isApiHealthy,
                    timestamp: new Date().toISOString()
                });

                // 4. Update health state with combined status
                setSocketHealth({
                    connected: isSocketConnected && isApiHealthy, // Both must be true
                    lastPing: Date.now(),
                    status: {
                        ...status.data,
                        socketConnected: isSocketConnected // Add socket state to status
                    },
                    error: null
                });

                // 5. Handle unhealthy state with more detail
                if (!isSocketConnected || !isApiHealthy) {
                    console.log('Health check failed:', {
                        socketConnected: isSocketConnected,
                        apiHealthy: isApiHealthy,
                        timestamp: new Date().toISOString()
                    });
                    
                    const event = new CustomEvent('socketUnhealthy', {
                        detail: { // Add detail for better error handling
                            socketConnected: isSocketConnected,
                            apiHealthy: isApiHealthy,
                            timestamp: Date.now()
                        }
                    });
                    window.dispatchEvent(event);

                    // Try to reconnect instead of logout
                    if (!isSocketConnected) {
                        await callService.attemptReconnection();
                    }
                }
            }
        } catch (error) {
            console.error('Socket health check failed:', {
                error: error.message,
                stack: error.stack,
                timestamp: new Date().toISOString()
            });
            
            setSocketHealth(prev => ({
                ...prev,
                connected: false,
                lastPing: Date.now(),
                error: error.message
            }));

            // Handle unauthorized without logout
            if (error.response?.status === 401) {
                // Redirect to login page if needed
                window.location.href = '/login';
                return;
            }

            // Try to recover socket on other errors
            try {
                await callService.attemptReconnection();
            } catch (reconnectError) {
                console.error('Reconnection failed:', reconnectError);
            }
        }
    }, HEARTBEAT_INTERVAL);
}, [isOnMatchScreen, callService]); 

  // Audio and Call Status Functions (depends on cleanup)
  const verifyAudioSetup = useCallback(async () => {
    try {
        const audioState = await agoraService.diagnoseMissingAudio();
        if (!audioState?.localTrack?.exists) {
            console.error('No local audio track found');
            setCallStatus('disconnected');
            cleanup();
            setMatch(null);
            return;
        }
        
        setIsMuted(!audioState.localTrack.enabled);
        setAudioStatus({
            local: audioState.localTrack.exists && audioState.localTrack.enabled,
            remote: audioState.remoteUsers?.some(u => u?.hasAudio && u?.audioEnabled) || false,
            volume: audioState.localTrack.volume || 0,
            quality: 'good'
        });
    } catch (error) {
        console.error('Audio setup verification error:', error);
        setCallStatus('disconnected');
        cleanup();
        setMatch(null);
    }
}, [cleanup]);

const handleMuteToggle = useCallback(async () => {
  if (callStatus !== 'connected') return;
  
  try {
      const previousState = isMuted;
      const isMutedNow = await agoraService.toggleMute();
      
      // Still keep state verification, but simpler
      if (isMutedNow === previousState) {
          console.warn('Volume state unchanged');
          // Get current volume to verify
          const diagnosis = await agoraService.diagnoseMissingAudio();
          setIsMuted(!diagnosis?.localTrack?.volume);
      } else {
          setIsMuted(isMutedNow);
      }
      
      setError(null);
  } catch (error) {
      console.error('Mute toggle error:', error);
      setError('Failed to toggle mute');
      
      // Keep fallback diagnosis for safety
      try {
          const diagnosis = await agoraService.diagnoseMissingAudio();
          setIsMuted(!diagnosis?.localTrack?.volume);
      } catch (stateError) {
          console.error('Failed to get audio state:', stateError);
      }
  }
}, [callStatus, isMuted]);

  const handleMatchTimeout = useCallback(async () => {
    try {
      setCallStatus('disconnected');
      setError('No match found. Please try again.');
      await apiHelpers.setUserUnavailable();
      setIsAvailableForMatch(false);
      
      if (matchTimeout.current) {
        clearTimeout(matchTimeout.current);
        matchTimeout.current = null;
      }
    } catch (error) {
      console.error('Match timeout handling error:', error);
    }
  }, []);

  const handleCallStatusChange = useCallback((status, matchData) => {
    console.log('Call status changed:', {
        newStatus: status,
        currentStatus: callStatus,
        hasMatch: !!(matchData || currentMatchRef.current),
        matchDetails: matchData || currentMatchRef.current,
        timestamp: new Date().toISOString()
    });

    try {
        // NEW: Handle peer_disconnected state
        if (status === 'peer_disconnected') {
            // Only update status, preserve all other state
            // This keeps the match data and call controls active
            setCallStatus('peer_disconnected');
            return;
        }

        // Modified: Enhanced connected state handling
        if (status === 'connected') {
            // NEW: Check if recovering from peer disconnect
            if (callStatus === 'peer_disconnected') {
                // Resuming existing call, maintain existing match data
                setCallStatus('connected');
                startCallDurationTracking('connected');
                startCallQualityMonitoring('connected');
                return;
            }

            // Existing connection logic for new calls
            const currentMatchData = matchData || currentMatchRef.current;
            if (!currentMatchData?.userId || !currentMatchData?.channelName) {
                console.error('Invalid match data:', currentMatchData);
                cleanup();
                setError('Invalid connection data');
                setCallStatus('disconnected');
                return;
            }

            // Update all states atomically
            const updatedMatchData = {
                ...currentMatchData,
                connectionTime: Date.now(),
                userId: currentMatchData.userId,
                username: currentMatchData.username,
                role: currentMatchData.role,
                channelName: currentMatchData.channelName,
                interests: currentMatchData.interests || '',
                mood: currentMatchData.mood || '',
                languages: currentMatchData.languages || ['English'],
                stars: currentMatchData.stars || 0,
                ratingCount: currentMatchData.ratingCount || 0
            };

            // Important: Reset finding state
            setIsAvailableForMatch(false);
            setIsProcessingMatch(false);

            // Update states in order
            setMatch(updatedMatchData);
            currentMatchRef.current = updatedMatchData;
            setCallStatus('connected');

            // Start monitoring with the new status explicitly
            startCallDurationTracking('connected');
            startCallQualityMonitoring('connected');

            console.log('Call connected successfully:', {
                matchData: updatedMatchData,
                timestamp: new Date().toISOString()
            });
            return;
        }

        // Existing: Handle error states
        if (status === 'error') {
            cleanup();
            setError('Call connection error. Please try again.');
            setCallStatus('disconnected');
            setMatch(null);
            setIsAvailableForMatch(false);
            return;
        }

        // Existing: Handle disconnected state
        if (status === 'disconnected') {
            cleanup();
            setMatch(null);
            currentMatchRef.current = null;
            setCallStatus(status);
            setIsAvailableForMatch(false);
            return;
        }

        // Existing: Handle rating state
        if (status === 'rating') {
            // Preserve match data for rating
            setCallStatus(status);
            return;
        }

        // Handle all other states
        setCallStatus(status);

    } catch (error) {
        console.error('Error in status change:', error);
        cleanup();
        setError('Error handling call status');
        setCallStatus('disconnected');
        setIsAvailableForMatch(false);
    }
}, [cleanup, startCallDurationTracking, startCallQualityMonitoring, callStatus]);


const handleMatchFound = useCallback(async (matchData) => {
  console.log('Match found event received:', {
      matchData,
      currentMatchStatus: currentMatchRef.current,
      timestamp: new Date().toISOString()
  });

  try {
      // Initial validation - enhanced to check all required fields
      if (!matchData?.userId || !matchData?.channelName || !matchData?.username) {
          console.error('Invalid match data received:', matchData);
          throw new Error('Invalid match data');
      }

      // Handle match timeout
      if (matchTimeout.current) {
          console.log('Clearing existing match timeout');
          clearTimeout(matchTimeout.current);
          matchTimeout.current = null;
      }

      // Audio validation first
      console.log('Verifying audio track');
      const audioValidation = await agoraService.verifyAudioTrack();
      console.log('Audio validation result:', {
          isValid: audioValidation?.isValid,
          details: audioValidation?.details
      });

      if (!audioValidation?.isValid) {
          throw new Error('Audio setup not ready');
      }

      // Enhanced match state setup
      console.log('Setting initial match state');
      const enhancedMatchData = {
          ...matchData,
          startTime: Date.now(),
          // Ensure all required fields for rating
          userId: matchData.userId,
          username: matchData.username,
          role: matchData.role,
          interests: matchData.interests || '',
          mood: matchData.mood || '',
          languages: matchData.languages || ['English'],
          stars: matchData.stars || 0,
          ratingCount: matchData.ratingCount || 0,
          channelName: matchData.channelName
      };

      // Update both ref and state with enhanced data
      currentMatchRef.current = enhancedMatchData;
      setMatch(enhancedMatchData);

      // Log complete match data for verification
      console.log('Match data stored:', {
          storedData: enhancedMatchData,
          refData: currentMatchRef.current,
          timestamp: new Date().toISOString()
      });

      // Wait for state update
      await new Promise(resolve => setTimeout(resolve, 100));

      // Join channel with role
      if (matchData.channelName) {
          console.log('Joining channel:', matchData.channelName);
          await callService.joinAsRole(isTalker ? 'talker' : 'listener');
          
          await new Promise(resolve => setTimeout(resolve, 100));
          
          const channelVerification = await agoraService.verifyChannelState(matchData.channelName);
          if (!channelVerification.verified) {
              throw new Error(`Channel join verification failed: ${channelVerification.reason}`);
          }
      }

      // Verify match data still exists
      if (currentMatchRef.current) {
          console.log('Starting call monitoring with match data:', {
              matchData: currentMatchRef.current,
              timestamp: new Date().toISOString()
          });

          startCallDurationTracking();
          startCallQualityMonitoring();
          
          setCallStatus('connected');
          setError(null);

          const diagnosis = await agoraService.diagnoseMissingAudio();
          console.log('Audio diagnosis result:', diagnosis);
          setDiagnosticInfo(diagnosis);

          const validationResult = await apiHelpers.validateCallState(matchData.channelName);
          if (!validationResult?.valid) {
              throw new Error('Call state validation failed');
          }

          // Emit ready state with enhanced data
          callService.socket?.emit('userReady', {
              channelName: matchData.channelName,
              audioReady: true,
              userId: matchData.userId,  // Add user identification
              role: isTalker ? 'talker' : 'listener'  // Add role
          });
      }

  } catch (error) {
      console.error('Match handling error:', {
          error: error.message,
          stack: error.stack,
          matchData,
          currentRef: currentMatchRef.current,  // Log ref state in error
          timestamp: new Date().toISOString()
      });

      setError(error.message || 'Failed to connect with match');
      setCallStatus('disconnected');
      setMatch(null);
      currentMatchRef.current = null;
      await cleanup();

      callService.socket?.emit('matchFailed', {
          channelName: matchData?.channelName,
          reason: error.message
      });
  }
}, [cleanup, startCallDurationTracking, startCallQualityMonitoring, isTalker]);

const handleBaseSocketError = useCallback((error) => {
  console.error('Socket error:', error);
  setConnectionStatus('error');
  socketInitialized.current = false;
}, []);

const initializeSocket = useCallback(async () => {
  try {
      // Check current socket state
      const currentSocket = window.socket || callService.socket;
      if (socketInitialized.current && currentSocket?.connected) {
          console.log('Socket already initialized and connected');
          return;
      }

      socketInitialized.current = false;
      setConnectionStatus('connecting');

      // Set connection timeout
      if (socketConnectionTimeout.current) {
          clearTimeout(socketConnectionTimeout.current);
      }
      socketConnectionTimeout.current = setTimeout(() => {
          if (connectionStatus === 'connecting') {
              handleBaseSocketError(new Error('Socket connection timeout'));
          }
      }, SOCKET_CONNECTION_TIMEOUT);

      // Attempt reconnection through callService
      await callService.attemptReconnection();

      // Verify socket connection
      const socket = window.socket || callService.socket;
      if (!socket?.connected) {
          if (socketRetryCount.current < maxRetryAttempts) {
              socketRetryCount.current += 1;
              console.log(`Retrying connection (${socketRetryCount.current}/${maxRetryAttempts})`);
              await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * socketRetryCount.current));
              return initializeSocket();
          } else {
              throw new Error('Max retry attempts reached');
          }
      }

      // Update states after successful reconnection
      setIsOnMatchScreen(true);
      socketInitialized.current = true;
      setConnectionStatus('connected');
      startSocketHealthCheck();

      if (socketConnectionTimeout.current) {
          clearTimeout(socketConnectionTimeout.current);
          socketConnectionTimeout.current = null;
      }

  } catch (error) {
      console.error('Socket reconnection error:', error);
      socketInitialized.current = false;
      handleBaseSocketError(error);
      if (socketRetryCount.current >= maxRetryAttempts) {
          setError('Connection lost. Please refresh the page.');
          cleanup();
      }
  }
}, [connectionStatus, audioPermissionGranted, startSocketHealthCheck, handleBaseSocketError]);

const handleRatingSubmit = async () => {
  const ratingMatch = match;
  
  console.log('Rating submission started:', {
      hasMatch: !!ratingMatch,
      userId: ratingMatch?.userId,
      rating: currentRating,
      timestamp: new Date().toISOString()
  });

  if (!ratingMatch?.userId || !currentRating || isSubmittingRating) {
      return;
  }

  setIsSubmittingRating(true);
  try {
      const response = await axiosInstance.post('/api/call/rate-listener', {
          listenerId: ratingMatch.userId,
          rating: currentRating,
          channelName: ratingMatch.channelName
      });

      if (response.data.success) {
          // Full cleanup after successful rating
          setShowRating(false);
          setCurrentRating(0);
          setMatch(null);  // Clear the match data
          currentMatchRef.current = null;  // Clear the ref
          await cleanup();
          await initializeSocket();
          setCallStatus('disconnected');
          setError(null);
      }
  } catch (error) {
      console.error('Rating submission error:', error);
      setError('Failed to submit rating');
  } finally {
      setIsSubmittingRating(false);
  }
};

const handleSkipRating = async () => {
  console.log('Rating skipped, cleaning up state');
  setShowRating(false);
  setCurrentRating(0);
  setMatch(null);  // Clear the match data
  currentMatchRef.current = null;  // Clear the ref
  await cleanup();
  await initializeSocket();
  setCallStatus('disconnected');
  setError(null);
};

const handleCallEnded = useCallback(async (reason, data) => {
  try {
      console.log('Call ended:', {
          reason,
          currentStatus: callStatus,
          isTalker: callService.myRole === 'talker',
          hasMatch: !!currentMatchRef.current,
          timestamp: new Date().toISOString()
      });

      if (callService.myRole === 'talker') {
          const matchData = data?.match || currentMatchRef.current;
          if (matchData) {
              setMatch(matchData);
              setShowRating(true);
              setCallStatus('rating');
              return; // Prevent cleanup until rating
          }
      }

      // Immediate cleanup for Listener or error cases
      await cleanup();
      await initializeSocket();
      setCallStatus('disconnected');

  } catch (error) {
      console.error('Error handling call end:', error);
      await cleanup();
      await initializeSocket();
      setCallStatus('disconnected');
  }
}, [cleanup, initializeSocket]);

const handleEndCall = async () => {
  try {
      if (callStatus === 'connected') {
          // Store match data before any cleanup
          const completeMatchData = {
              ...match,
              timestamp: Date.now()
          };

          console.log('End call initiated:', {
              myRole: callService.myRole,
              hasMatch: !!completeMatchData,
              matchData: completeMatchData,
              timestamp: new Date().toISOString()
          });

          // Set disconnecting state
          setCallStatus('disconnecting');

          // Call endCall without matchData (let socket event handle data)
          await callService.endCall();

          // Simple flow based on role
          if (callService.myRole === 'talker') {
              setMatch(completeMatchData);
              setShowRating(true);
          } else {
              // Listener: Immediate cleanup and reset
              setCallStatus('disconnected');
              setMatch(null);
              currentMatchRef.current = null;
              await initializeSocket();
          }
      }
  } catch (error) {
      console.error('End call error:', error);
      setError('Failed to end call properly');
      setCallStatus('disconnected');

      // Clean up only if Listener
      if (callService.myRole !== 'talker') {
          setMatch(null);
          currentMatchRef.current = null;
          await initializeSocket();
      }
  }
};

const handleLogout = useCallback(async () => {
  try {
    if (callStatus === 'connected') {
      await handleEndCall();
    }
    await cleanup();
    logout();
  } catch (error) {
    console.error('Logout error:', error);
    logout(); // Force logout even if there's an error
  }
}, [callStatus, cleanup, logout]);


  // Device Management (depends on initializeCall)
  const handleAudioDeviceChange = useCallback(async (deviceId) => {
    try {
        setSelectedAudioDevice(deviceId);
        
        if (callStatus === 'connected') {
            await cleanup();
            await callService.initialize(
                handleCallStatusChange,
                handleMatchFound,
                handleCallEnded,
                handleCallQualityUpdate,
                handleRemoteUserEvent
            );
            
            // Changed to use agoraService directly
            const diagnosis = await agoraService.diagnoseMissingAudio();
            if (!diagnosis?.localTrack?.exists) {
                throw new Error('Failed to initialize new audio device');
            }
        }
    } catch (error) {
        console.error('Error changing audio device:', error);
        setError('Failed to switch audio device. Please reconnect.');
    }
}, [
    callStatus,
    cleanup,
    handleCallStatusChange,
    handleMatchFound,
    handleCallEnded,
    handleCallQualityUpdate,
    handleRemoteUserEvent
]);

const handleDeviceChange = useCallback(async () => {
  if (callStatus === 'connected') {
      try {
          await cleanup();
          await setupAudioDevices();
          
          if (match) {
              // Initialize call service directly here
              await callService.initialize(
                  handleCallStatusChange,
                  handleMatchFound,
                  handleCallEnded,
                  handleCallQualityUpdate,
                  handleRemoteUserEvent
              );
              
              // Rejoin channel after reinitializing
              if (match.channelName) {
                  await callService.joinAsRole(isTalker ? 'talker' : 'listener');
              }
          }
      } catch (error) {
          console.error('Device change handler error:', error);
          setError('Failed to handle device change');
      }
  }
}, [
  callStatus,
  cleanup,
  setupAudioDevices,
  match,
  isTalker,
  handleCallStatusChange,
  handleMatchFound,
  handleCallEnded,
  handleCallQualityUpdate,
  handleRemoteUserEvent
]);

const handleRoleToggle = useCallback(async () => {
  if (callStatus === 'connected' || callStatus === 'finding' || isProcessingMatch) {
      return;
  }

  try {
      const newRole = !isTalker;
      await cleanup();
      
      const response = await axiosInstance.put('/api/auth/user/role', {
          role: newRole ? 'talker' : 'listener'
      });

      if (response.data.user) {
          // First emit the role update
          window.socket.emit('roleUpdate', {
              role: newRole ? 'talker' : 'listener'
          });

          // Wait a tiny bit for socket event to process
          await new Promise(resolve => setTimeout(resolve, 50));

          // Then update local state
          setIsTalker(newRole);
          localStorage.setItem('userRole', newRole ? 'talker' : 'listener');
          
          // Reset other states
          setMatch(null);
          currentMatchRef.current = null;
          setCallStatus('disconnected');
          setShowRating(false);
          setCurrentRating(0);
          setError(null);

          // Finally reinitialize services
          await callService.initialize(
              handleCallStatusChange,
              handleMatchFound,
              handleCallEnded,
              handleCallQualityUpdate,
              handleRemoteUserEvent
          );
          
          await fetchUserInfo();
      }
  } catch (error) {
      console.error('Role switch error:', error);
      setError('Failed to switch role');
  }
}, [isTalker, callStatus, isProcessingMatch, cleanup, fetchUserInfo]);

  // In Match.js
  const handleFindMatch = useCallback(async () => {
    console.log('Find Match function called:', {
        isProcessingMatch,
        isOnMatchScreen,
        currentRole: isTalker ? 'talker' : 'listener',
        socketStatus: socketHealth.connected
    });

    if (isProcessingMatch || !isOnMatchScreen) {
        console.log('Find Match aborted:', { 
            reason: isProcessingMatch ? 'Match processing in progress' : 'Not on match screen' 
        });
        return;
    }

    try {
        setIsProcessingMatch(true);
        setError(null);

        // 1. Audio Check First
        console.log('Checking audio permissions and setup');
        const audioSetup = await agoraService.verifyAudioTrack();
        console.log('Audio setup verification:', audioSetup);
        
        if (!audioSetup.isValid) {
            throw new Error('Audio setup not ready. Please check your microphone.');
        }

        const audioPermission = await checkAndRequestAudioPermission();
        if (!audioPermission) {
            throw new Error('Microphone access is required for matching');
        }

        // 2. Socket Connection Check and Setup
        const socket = window.socket || callService.socket;
        if (!socket?.connected) {
            console.log('Socket disconnected, attempting reconnection');
            await callService.attemptReconnection();
            
            // Double check connection after reconnection attempt
            if (!callService.socket?.connected) {
                throw new Error('Unable to establish connection. Please refresh the page.');
            }
            
            console.log('Socket reconnection successful:', {
                connected: callService.socket.connected,
                socketId: callService.socket.id,
                timestamp: new Date().toISOString()
            });
        }

        // 3. Set Enhanced User Availability
        console.log('Setting user availability');
        const availabilityResponse = await apiHelpers.setUserAvailable({
            role: isTalker ? 'talker' : 'listener',
            deviceInfo: {
                type: /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
                browser: navigator.userAgent,
                platform: navigator.platform,
                audioEnabled: true,
                timestamp: Date.now()
            },
            matchingPreferences: {
                role: isTalker ? 'talker' : 'listener',
                languages: ['English'],
                availability: 'anytime',
                audioQuality: audioSetup.details?.volume || 0
            }
        });

        console.log('Availability response:', availabilityResponse);

        // 4. Update Local State
        setIsAvailableForMatch(true);
        setCallStatus('finding');

        // 5. Final Socket Check and Emit
        const currentSocket = callService.socket;
        if (!currentSocket?.connected) {
            throw new Error('Lost connection while preparing match.');
        }

        console.log('Emitting match request:', {
            socketId: currentSocket.id,
            role: isTalker ? 'talker' : 'listener',
            timestamp: new Date().toISOString()
        });

        // 6. Enhanced Match Request
        currentSocket.emit('findMatch', {
            role: isTalker ? 'talker' : 'listener',
            timestamp: Date.now(),
            audioReady: true,
            deviceInfo: {
                type: /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
                browser: navigator.userAgent,
                audioEnabled: true
            }
        });

        // 7. Setup Match Timeout with Enhanced Handling
        if (matchTimeout.current) {
            clearTimeout(matchTimeout.current);
        }

        matchTimeout.current = setTimeout(() => {
            if (callStatus === 'finding') {
                console.log('Match timeout triggered');
                handleMatchTimeout();
                // Attempt to recover availability state
                apiHelpers.setUserAvailable({
                    role: isTalker ? 'talker' : 'listener',
                    retry: true
                }).catch(console.error);
            }
        }, MATCH_TIMEOUT);

        // 8. Start Periodic State Check
        const stateCheckInterval = setInterval(async () => {
            if (callStatus === 'finding') {
                try {
                    const stateResponse = await axiosInstance.get('/api/auth/user/active-status');
                    if (!stateResponse.data.isAvailable) {
                        console.log('Recovering availability state');
                        await apiHelpers.setUserAvailable({
                            role: isTalker ? 'talker' : 'listener'
                        });
                    }
                } catch (error) {
                    console.error('State check error:', error);
                }
            } else {
                clearInterval(stateCheckInterval);
            }
        }, 10000); // Check every 10 seconds

        return () => {
            clearInterval(stateCheckInterval);
        };

    } catch (error) {
        console.error('Find match error:', {
            error,
            message: error.message,
            stack: error.stack,
            timestamp: new Date().toISOString()
        });

        // Enhanced error handling
        setError(error.message || 'Unable to start matching. Please try again.');
        setCallStatus('disconnected');
        setIsAvailableForMatch(false);

        // Attempt state recovery
        try {
            await apiHelpers.setUserUnavailable();
            if (callService.socket?.connected) {
                callService.socket.emit('cancelMatch', {
                    timestamp: Date.now(),
                    reason: 'error'
                });
            }
        } catch (recoveryError) {
            console.error('Error recovery failed:', recoveryError);
        }

    } finally {
        console.log('Find match process completed:', {
            final_status: callStatus,
            isAvailable: isAvailableForMatch,
            socketConnected: callService.socket?.connected,
            timestamp: new Date().toISOString()
        });
        setIsProcessingMatch(false);
    }
}, [
    isProcessingMatch,
    isOnMatchScreen,
    isTalker,
    callStatus,
    socketHealth.connected,
    callService,
    handleMatchTimeout
]);

  // Effect Hooks
  useEffect(() => {
    let mounted = true;
    
    const initializeApp = async () => {
        try {
            if (mounted) {
                // 1. Basic Setup
                await checkAndRequestAudioPermission();
                const storedRole = localStorage.getItem('userRole');
                console.log('Initial role setup:', {
                  storedRole,
                  isTalker: storedRole ? storedRole.toLowerCase() === 'talker' : true
              });
                setIsTalker(storedRole ? storedRole.toLowerCase() === 'talker' : true);

                // 2. Initial call service setup (includes first socket connection)
                await callService.initialize(
                    handleCallStatusChange,
                    handleMatchFound,
                    handleCallEnded,
                    handleCallQualityUpdate,
                    handleRemoteUserEvent
                );
                
                // 3. Set initial states
                socketInitialized.current = true;
                setIsOnMatchScreen(true);
                setConnectionStatus('connected');
                
                // 4. Start monitoring
                startSocketHealthCheck();

                // 5. Additional Setup
                await fetchUserInfo();
                await setupAudioDevices();
            }
        } catch (error) {
            if (mounted) {
                console.error('Initialization error:', error);
                setError('Failed to initialize. Please refresh the page.');
            }
        }
    };

    initializeApp();

    return () => {
        mounted = false;
        cleanup();
    };
}, []);

  useEffect(() => {
    syncUserState();
    const interval = setInterval(syncUserState, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    navigator.mediaDevices?.addEventListener('devicechange', handleDeviceChange);
    return () => {
      navigator.mediaDevices?.removeEventListener('devicechange', handleDeviceChange);
    };
  }, [handleDeviceChange]);

  useEffect(() => {
    if (isOnMatchScreen) {
      userInfoIntervalRef.current = setInterval(() => {
        if (!isEditingInfo) {
          fetchUserInfo();
        }
      }, 30000);
    }

    return () => {
      if (userInfoIntervalRef.current) {
        clearInterval(userInfoIntervalRef.current);
      }
    };
  }, [isOnMatchScreen, isEditingInfo]);

  // Add to Match.js useEffect
  useEffect(() => {
    const handleVisibilityChange = () => {
        if (document.visibilityState === "hidden") {
            console.log("Tab hidden, keeping socket alive...");
            // Send immediate heartbeat when tab hidden
            window.socket?.emit('heartbeat');
        } else {
            console.log("Tab visible, checking socket...");
            if (!window.socket?.connected) {
                initializeSocket();
            }
        }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
}, []);

useEffect(() => {
  const handleSocketUnhealthy = async () => {
    try {
      await initializeSocket();
    } catch (error) {
      console.error('Failed to reinitialize socket:', error);
    }
  };

  window.addEventListener('socketUnhealthy', handleSocketUnhealthy);
  return () => {
    window.removeEventListener('socketUnhealthy', handleSocketUnhealthy);
  };
}, [initializeSocket]);

useEffect(() => {
  // Start monitoring as soon as component mounts
  startAudioMonitoring();
  
  return () => {
      if (audioMonitoringRef.current) {
          clearInterval(audioMonitoringRef.current);
      }
  };
}, [startAudioMonitoring]);

useEffect(() => {
  let matchSyncInterval;
  let stateCheckInterval;
  let socketCheckInterval;  // Add this
  
  if (callStatus === 'finding') {
      // Keep existing matchSyncInterval logic
  }

  // Combined state validation and socket check for connected state
  if (callStatus === 'connected' || callStatus === 'finding') {
      stateCheckInterval = setInterval(async () => {
          try {
              // 1. Check socket connection first
              const socket = window.socket || callService.socket;
              if (!socket?.connected && callStatus === 'connected') {
                  console.log('Socket disconnected during call, cleaning up');
                  await handleEndCall();
                  return;  // Exit early if socket disconnected
              }

              // 2. Then do state validation
              if (!match?.channelName) {
                  console.log('Skipping state validation - no channel name available');
                  return;
              }

              const response = await axiosInstance.get(`/api/call/validate-state`, {
                  params: {
                      channelName: match.channelName,
                      _t: Date.now()
                  }
              });

              console.log('State validation response:', {
                  channelName: match.channelName,
                  response: response.data
              });
              
              if (!response.data.valid && callStatus === 'connected') {
                  console.log('Invalid call state detected, cleaning up...');
                  // Force cleanup all services
                  await agoraService.cleanup();
                  await callService.cleanup(true);
                  
                  // Reset UI state
                  setCallStatus('disconnected');
                  setMatch(null);
                  currentMatchRef.current = null;
                  setIsAvailableForMatch(false);
                  
                  // Reinitialize socket
                  await initializeSocket();
              }
          } catch (error) {
              console.error('State validation error:', {
                  error,
                  currentStatus: callStatus,
                  hasChannelName: !!match?.channelName,
                  channelName: match?.channelName
              });
          }
      }, 5000);  // Run every 5 seconds
  }

  // Cleanup function
  return () => {
      [matchSyncInterval, stateCheckInterval, socketCheckInterval].forEach(interval => {
          if (interval) clearInterval(interval);
      });
  };
}, [callStatus, isTalker, callService, match?.channelName, handleEndCall]);  // Add handleEndCall to deps

useEffect(() => {
  const socket = window.socket || callService.socket;
  
  if (socket) {
      console.log('Setting up socket listeners');
      
      const listeners = {
          waitingForMatch: () => {
              console.log('Received waitingForMatch event');
          },
          matchFound: (data) => {
              console.log('Received matchFound event:', data);
              handleMatchFound(data).catch(error => {
                  console.error('Error processing match:', error);
                  setError('Failed to process match');
              });
          },
          connect: () => {
              console.log('Socket connected event');
              setSocketHealth(prev => ({
                  ...prev,
                  connected: true,
                  lastPing: Date.now()
              }));
          },
          disconnect: () => {
              console.log('Socket disconnected event');
              setSocketHealth(prev => ({
                  ...prev,
                  connected: false,
                  lastPing: Date.now()
              }));
          },
          callEnded: async (data) => {
              console.log('Call ended event:', {
                  serviceRole: callService.myRole,
                  hasMatch: !!match,
                  currentStatus: callStatus,
                  timestamp: new Date().toISOString()
              });

              try {
                  if (callService.myRole === 'talker') {
                      // Talker flow: Show rating
                      const completeMatchData = {
                          ...match,
                          timestamp: Date.now()
                      };

                      // Clean up audio only
                      await agoraService.cleanup();

                      // Prepare rating state
                      setMatch(completeMatchData);
                      setShowRating(true);
                      setCallStatus('rating');
                  } else {
                      // Listener flow: Immediate cleanup
                      console.log('Starting listener cleanup');
                      await callService.cleanup(true);
                      setCallStatus('disconnected');
                      setMatch(null);
                      currentMatchRef.current = null;
                      await initializeSocket();
                  }
              } catch (error) {
                  console.error('Error in callEnded handler:', error);
                  setCallStatus('disconnected');
                  if (callService.myRole === 'listener') {
                      setMatch(null);
                      currentMatchRef.current = null;
                      await initializeSocket();
                  }
              }
          }
      };

      Object.entries(listeners).forEach(([event, handler]) => {
          socket.on(event, handler);
      });

      return () => {
          console.log('Cleaning up socket listeners');
          Object.keys(listeners).forEach(event => {
              socket.off(event);
          });
      };
  }
}, [callService]); // Only callService needed as dependency

useEffect(() => {
  const setupSocketListener = () => {
      const socket = window.socket;
      
      if (socket && socket.connected) {
          console.log('Setting up role count listener, socket ID:', socket.id);

          const handleRoleCounts = (counts) => {
              console.log('Role counts event received:', {
                  counts,
                  currentSocketId: socket.id,
                  currentRole: isTalker ? 'talker' : 'listener',
                  timestamp: new Date().toISOString()
              });

              // Force update for both broadcast and self-updates
              if (counts && typeof counts.talker === 'number' && typeof counts.listener === 'number') {
                  console.log('Setting role counts:', counts);
                  setRoleCounts(counts);
              }
          };

          // Listen for role counts
          socket.on('roleCounts', handleRoleCounts);

          // Request initial counts when connecting
          socket.emit('requestRoleCounts');

          // Update counts on reconnection
          socket.on('connect', () => {
              socket.emit('requestRoleCounts');
          });

          // Add roleUpdateConfirmed listener
          socket.on('roleUpdateConfirmed', (data) => {
              console.log('Role update confirmed:', data);
              socket.emit('requestRoleCounts');
          });

          return () => {
              console.log('Cleaning up role count listener');
              socket.off('roleCounts', handleRoleCounts);
              socket.off('connect');
              socket.off('roleUpdateConfirmed');
          };
      }
  };

  // Initial setup attempt
  let cleanup = setupSocketListener();

  // Set up an interval to check for socket connection
  const checkInterval = setInterval(() => {
      if (window.socket?.connected && !cleanup) {
          console.log('Socket now available, setting up listener');
          cleanup = setupSocketListener();
      }
  }, 1000);

  // Cleanup function
  return () => {
      if (cleanup) cleanup();
      clearInterval(checkInterval);
  };
}, [isTalker, callStatus]); // Added isTalker to dependencies

useEffect(() => {
  const handleClickOutside = (e) => {
      // Only close if clicking outside both the language selector and dropdown
      const container = e.target.closest('.language-container');
      if (isEditingLanguages && !container) {
          setIsEditingLanguages(false);
      }
  };

  window.addEventListener('click', handleClickOutside);
  return () => window.removeEventListener('click', handleClickOutside);
}, [isEditingLanguages]);

  // Helper UI Components
  const ConnectionStatus = () => (
    <div className="flex items-center space-x-2">
      <Circle 
        className={`w-2 h-2 ${
          connectionStatus === 'connected' ? 'text-teal-500 fill-current' :
          connectionStatus === 'connecting' ? 'text-amber-500 fill-current' :
          'text-rose-500 fill-current'
        } ${socketHealth.connected ? 'animate-pulse' : ''}`}
      />
      <span className="text-gray-600 text-sm">
        {connectionStatus === 'connected' ? 'Connected' :
         connectionStatus === 'connecting' ? 'Connecting...' :
         'Connection Error'}
        {socketHealth.connected && connectionStatus === 'connected' && 
          <span className="text-xs ml-2 text-gray-500">
            ({Math.floor((Date.now() - socketHealth.lastPing) / 1000)}s ago)
          </span>
        }
      </span>
    </div>
  );

  const AudioStatus = () => {
    // Add comprehensive logging before renderr
    console.log('Full audio status:', audioStatus);
    console.log('Volume value:', audioStatus?.volume);
    console.log('Local track details:', audioStatus?.rawDiagnosis?.localTrack);
    
    return (
      <div className="flex items-center space-x-4">
        <div className="flex items-center space-x-2">
          <Mic 
            className={`w-4 h-4 ${
              audioStatus?.volume > 0.01 ? 'text-teal-500' : 'text-rose-500'
            }`} 
          />
          <span className="text-sm text-gray-600">
            {audioStatus?.volume > 0.01 ? 'Mic Active' : 'Mic Inactive'}
            <span className="text-xs ml-1 text-gray-500">
              ({(audioStatus?.volume || 0).toFixed(3)})
            </span>
          </span>
        </div>
      </div>
    );
  };

const CallQualityIndicator = () => {
  if (!match || callStatus !== 'connected') {
      return null;
  }

  return (
    <div className="flex items-center space-x-2">
      <div className={`w-1.5 h-1.5 rounded-full ${
        callQuality.status === 'good' ? 'bg-teal-500' :
        callQuality.status === 'warning' ? 'bg-amber-500' :
        'bg-rose-500'
      }`} />
      <span className="text-sm text-gray-600">
        {callQuality.status === 'good' ? 'Good Connection' :
         callQuality.status === 'warning' ? 'Fair Connection' :
         'Poor Connection'}
      </span>
    </div>
  );
};

// Add handler similar to interests/mood
const handleLanguagesChange = useCallback(
  _.debounce(async (value) => {
      try {
          await axiosInstance.put('/api/auth/user/update-info', {
              languages: value,
              timestamp: Date.now()
          });
      } catch (error) {
          console.error('Error updating languages:', error);
      }
  }, 500),
  []
);

const containsBlockedWords = (text) => {
  const words = text.toLowerCase().split(' ');
  return words.some(word => BLOCKED_WORDS.includes(word.trim()));
};

// Helper function to sanitize input
const sanitizeInput = (text) => {
  return text.replace(/[^a-zA-Z0-9\s.,!?-]/g, '');
};

  // Main render
  return (
    <TutorialProvider>
  <div className="min-h-screen bg-gray-50">
    {/* Header - Visual changes only */}
    <div className="bg-white border-b border-gray-100 px-4 py-3">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-xl font-bold text-teal-500">TalkConnect</h1>
          <ConnectionStatus />
        </div>
        <button
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          className="p-2.5 rounded-full hover:bg-gray-100 text-teal-500 transition-colors"
          aria-label="Open profile sidebar"
        >
          <User className="w-5 h-5" />
        </button>
      </div>

       {/* Main Content Area */}
    <div className="px-4 py-3"></div>
        {/* Connection Error Display */}
      {connectionStatus === 'error' && (
        <div className="bg-rose-50 border border-rose-200 text-rose-600 px-4 py-3 rounded-lg mb-3">
          <div className="flex items-center">
            <AlertTriangle className="h-5 w-5 mr-2" />
            <span className="font-medium">
              {error || 'Unable to connect to server. Please try refreshing.'}
            </span>
          </div>
        </div>
      )}

        {/* User Info Box - Only shown when not in a call */}
{!match && (
  <div className="bg-white rounded-lg shadow-sm p-3 mb-3">
    <div className="space-y-2">
      {/* Header */}
      <h2 className="text-gray-700 text-lg mb-2">Share what's on your mind</h2>

      {/* Interests */}
      <div 
        className="bg-gray-50 rounded-md p-2 cursor-text hover:bg-gray-100 transition-colors"
        onClick={() => setIsEditingInterests(true)}
      >
        <div className="flex items-center justify-between mb-0.5">
          <span className="text-teal-500 text-sm font-medium">Interests</span>
          <Edit className="w-3.5 h-3.5 text-gray-400" />
        </div>
        {isEditingInterests ? (
          <input
            type="text"
            value={interests}
            onChange={(e) => {
              const sanitizedValue = sanitizeInput(e.target.value);
              if (!containsBlockedWords(sanitizedValue)) {
                setInterests(sanitizedValue);
                handleInterestsChange(sanitizedValue);
              } else {
                setError('Please keep content appropriate');
                // Optional: Remove the blocked word
                const cleanValue = sanitizedValue.split(' ')
                .filter(word => !BLOCKED_WORDS.includes(word.toLowerCase()))
                .join(' ');
                setInterests(cleanValue);
                handleInterestsChange(cleanValue);
              }
            }}
            onBlur={() => setIsEditingInterests(false)}
            className="w-full bg-transparent focus:outline-none text-gray-700"
            placeholder="What are you interested in?"
            autoFocus
            maxLength={50}
          />
        ) : (
          <span className="text-gray-700">{interests || "What are you interested in?"}</span>
        )}
      </div>

      {/* Mood */}
      <div 
        className="bg-gray-50 rounded-md p-2 cursor-text hover:bg-gray-100 transition-colors"
        onClick={() => setIsEditingMood(true)}
      >
        <div className="flex items-center justify-between mb-0.5">
          <span className="text-teal-500 text-sm font-medium">Mood</span>
          <Edit className="w-3.5 h-3.5 text-gray-400" />
        </div>
        {isEditingMood ? (
          <input
            type="text"
            value={mood}
            onChange={(e) => {
              const sanitizedValue = sanitizeInput(e.target.value);
              if (!containsBlockedWords(sanitizedValue)) {
                setMood(sanitizedValue);
                handleMoodChange(sanitizedValue);
              } else {
                setError('Please keep content appropriate');
                // Optional: Remove the blocked word
                const cleanValue = sanitizedValue.split(' ')
                .filter(word => !BLOCKED_WORDS.includes(word.toLowerCase()))
                .join(' ');
                setMood(cleanValue);
                handleMoodChange(cleanValue);
              }
            }}
            onBlur={() => setIsEditingMood(false)}
            className="w-full bg-transparent focus:outline-none text-gray-700"
            placeholder="How are you feeling?"
            autoFocus
            maxLength={30}
          />
        ) : (
          <span className="text-gray-700">{mood || "How are you feeling?"}</span>
        )}
      </div>

      {/* Languages */}
      <div className="bg-gray-50 rounded-md p-2 hover:bg-gray-100 transition-colors relative language-container">
    {/* Header - clickable to open dropdown */}
    <div 
        className="cursor-pointer"
        onClick={() => setIsEditingLanguages(!isEditingLanguages)}
    >
        <div className="flex items-center justify-between mb-0.5">
            <span className="text-teal-500 text-sm font-medium">Languages</span>
            <Edit className="w-3.5 h-3.5 text-gray-400" />
        </div>
        
        {/* Selected Languages as Pills */}
        <div className="flex flex-wrap gap-2">
            {languages.length > 0 ? (
                languages.map((lang) => (
                    <div 
                        key={lang}
                        className="bg-teal-100 text-teal-700 px-2 py-1 rounded-full text-sm flex items-center"
                    >
                        {lang}
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                const newLanguages = languages.filter(l => l !== lang);
                                setLanguages(newLanguages);
                                handleLanguagesChange(newLanguages);
                            }}
                            className="ml-1 text-teal-500 hover:text-teal-700"
                        >
                            <XIcon className="w-3 h-3" />
                        </button>
                    </div>
                ))
            ) : (
                <span className="text-gray-700">What languages do you speak?</span>
            )}
        </div>
    </div>

    {/* Dropdown Menu */}
    {isEditingLanguages && (
        <div className="absolute left-0 right-0 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
            {[
                "English", "Spanish", "French", "German", 
                "Chinese", "Japanese", "Korean", "Arabic", 
                "Hindi", "Russian"
            ]
            .filter(lang => !languages.includes(lang))
            .map((lang) => (
                <div
                    key={lang}
                    className="px-4 py-2 hover:bg-gray-50 cursor-pointer"
                    onClick={(e) => {
                        e.stopPropagation();
                        if (languages.length < 3) {
                            const newLanguages = [...languages, lang];
                            setLanguages(newLanguages);
                            handleLanguagesChange(newLanguages);
                        }
                    }}
                >
                    <span className="text-gray-700">{lang}</span>
                </div>
            ))}
            {languages.length >= 3 && (
                <div className="px-4 py-2 text-xs text-rose-500 border-t border-gray-100">
                    Maximum 3 languages selected
                </div>
            )}
        </div>
    )}
</div>

      {/* Error Message */}
      {error && (
        <div className="text-rose-500 text-sm mt-2 text-center">
          {error}
        </div>
      )}
    </div>
  </div>
)}

{!match && (
  <div className="bg-white rounded-lg shadow-sm p-4 mb-3">
    <h3 className="text-center text-gray-700 font-medium mb-4">
      Who's available to Connect?
    </h3>

    {/* Centered container with reduced spacing */}
    <div className="flex flex-col">
      {/* Role Counts and Toggle Row - Centered and tighter spacing */}
      <div className="flex items-center justify-center gap-8"> {/* Removed mb-4 since it's now standalone */}
        {/* Talkers Side */}
        <div className="flex flex-col items-center">
          <div className="bg-teal-50 w-12 h-12 rounded-full flex items-center justify-center mb-2 relative">
            <Mic className="w-5 h-5 text-teal-500" />
            <div className="absolute -top-1 -right-1 bg-teal-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
              {roleCounts.talker || 0}
            </div>
          </div>
          <span className={`text-sm font-medium text-gray-700 ${isTalker ? "text-teal-500" : ""}`}>
            Talkers
          </span>
        </div>

        {/* Role Toggle Switch */}
        <div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={!isTalker}
              onChange={handleRoleToggle}
              disabled={callStatus === 'connected' || callStatus === 'finding'}
              className="sr-only"
            />
            <div className={`w-14 h-7 rounded-full transition-colors border-2
              ${!isTalker 
                ? 'bg-teal-500 border-teal-500' 
                : 'bg-gray-200 border-gray-200'} 
              ${(callStatus === 'connected' || callStatus === 'finding') 
                ? 'opacity-50 cursor-not-allowed' 
                : ''}`}>
              <div className={`absolute w-5 h-5 bg-white rounded-full transition-transform 
                top-1 left-1 shadow-sm ${!isTalker ? 'translate-x-7' : 'translate-x-0'}`} />
            </div>
          </label>
        </div>

        {/* Listeners Side */}
        <div className="flex flex-col items-center">
          <div className="bg-teal-50 w-12 h-12 rounded-full flex items-center justify-center mb-2 relative">
            <Headphones className="w-5 h-5 text-teal-500" />
            <div className="absolute -top-1 -right-1 bg-teal-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
              {roleCounts.listener || 0}
            </div>
          </div>
          <span className={`text-sm font-medium text-gray-700 ${!isTalker ? "text-teal-500" : ""}`}>
            Listeners
          </span>
        </div>
      </div>
    </div>
  </div>
)}

{callStatus === 'finding' && (
  <div className="bg-white rounded-lg shadow-sm p-4 mb-3">
    <div className="text-center">
      <div className="text-lg font-medium text-amber-500">
        Finding a match...
      </div>
    </div>
  </div>
)}

{callStatus === 'peer_disconnected' && (
  <div className="bg-amber-50 p-4 rounded-lg mb-3">
    <div className="flex items-center">
      <AlertTriangle className="text-amber-500 w-5 h-5 mr-2" />
      <span className="text-amber-700 font-medium">
        Peer Disconnected... Reconnecting
      </span>
    </div>
  </div>
)}

{match && (
  <div className="bg-white rounded-lg shadow-sm">
    {/* Call Duration Banner */}
    {(callStatus === 'connected' || callStatus === 'peer_disconnected') && (
      <div className={`px-4 py-2 flex items-center justify-between border-b 
        ${callStatus === 'peer_disconnected' 
          ? 'bg-amber-50 border-amber-100' 
          : 'bg-teal-500/10 border-teal-500/20'
        }`}>
        <div className="flex items-center">
          <div className={`w-2 h-2 rounded-full mr-2 
            ${callStatus === 'peer_disconnected'
              ? 'bg-amber-500'
              : 'bg-teal-500 animate-pulse'
            }`} />
          <span className={
            callStatus === 'peer_disconnected'
              ? 'text-amber-700 font-medium'
              : 'text-teal-700 font-medium'
          }>
            {callStatus === 'peer_disconnected' 
              ? 'Peer Disconnected'
              : 'In Call'
            }
          </span>
        </div>
        <span className="text-teal-600 font-medium tabular-nums">
          {Math.floor(callDuration / 60)}:{(callDuration % 60).toString().padStart(2, '0')}
        </span>
      </div>
    )}
    {/* Match Details */}
    <div className="p-4">
      <h2 className="text-lg font-semibold text-gray-900 mb-2">
        Connected with {match.username}
      </h2>
      
      {match.stars !== undefined && (
        <div className="flex items-center text-gray-600 mb-3">
          <Star className="w-4 h-4 text-yellow-400 fill-current mr-1" />
          <span>{match.stars.toFixed(1)} ({match.ratingCount} ratings)</span>
        </div>
      )}
      
      <div className="space-y-3">
        <div className="bg-gray-50 rounded-md p-2.5">
          <span className="text-teal-500 font-medium">Their Interests: </span>
          <span className="text-gray-700">{match.interests || "Not specified"}</span>
        </div>
        <div className="bg-gray-50 rounded-md p-2.5">
          <span className="text-teal-500 font-medium">Their Mood: </span>
          <span className="text-gray-700">{match.mood || "Not specified"}</span>
        </div>
        <div className="bg-gray-50 rounded-md p-2.5">
          <span className="text-teal-500 font-medium">Their Languages: </span>
          <span className="text-gray-700">
            {match.languages?.join(', ') || "Not specified"}
          </span>
        </div>
      </div>
    </div>
  </div>
)}

        {/* Audio Settings
        {audioDevices.length > 0 && (
          <div className={baseClasses.card}>
            <div className="flex justify-between items-center mb-4">
              <h3 className={`${baseClasses.heading} mb-2`}>Audio Settings</h3>
              <AudioStatus />
            </div>
            <select
              value={selectedAudioDevice}
              onChange={(e) => handleAudioDeviceChange(e.target.value)}
              className={baseClasses.input}
              disabled={callStatus === 'connected' || callStatus === 'finding'}
            >
              {audioDevices.map(device => (
                <option key={device.deviceId} value={device.deviceId}>
                  {device.label || `Microphone ${device.deviceId.slice(0, 8)}`}
                </option>
              ))}
            </select>
            {diagnosticInfo && (
              <div className="mt-2 text-sm text-gray-500">
                <div>Local Audio: {diagnosticInfo.localAudio ? 'Ready' : 'Not Ready'}</div>
                <div>Audio Enabled: {diagnosticInfo.localEnabled ? 'Yes' : 'No'}</div>
                <div>Connected Users: {diagnosticInfo.remoteUsers}</div>
              </div>
            )}
          </div>
        )} */}

    

{/* Match Display - High Priority */}
{/* {match && (
  <div className="bg-white rounded-lg shadow-sm p-4 mb-3">
    <h2 className="text-lg font-semibold text-gray-900 mb-2">
      Connected with {match.username}
    </h2>
    {match.stars !== undefined && (
      <div className="flex items-center text-gray-600 mb-3">
        <Star className="w-4 h-4 text-yellow-400 fill-current mr-1" />
        <span>{match.stars.toFixed(1)} ({match.ratingCount} ratings)</span>
      </div>
    )}
    
    <div className="space-y-3">
      <div className="bg-gray-50 rounded-md p-2.5">
        <span className="text-teal-500 font-medium">Their Interests: </span>
        <span className="text-gray-700">{match.interests || "Not specified"}</span>
      </div>
      <div className="bg-gray-50 rounded-md p-2.5">
        <span className="text-teal-500 font-medium">Their Mood: </span>
        <span className="text-gray-700">{match.mood || "Not specified"}</span>
      </div>
      {match.isMuted && (
        <div className="flex items-center text-amber-500 bg-amber-50 px-3 py-1.5 rounded-full">
          <MicOff className="w-4 h-4 mr-1.5" />
          <span>User is muted</span>
        </div>
      )}
    </div>
  </div>
)} */}

          {/* App Description and Support Section */}
<div className="bg-white rounded-lg shadow-sm p-4 mb-3">
  <div className="flex flex-col items-center text-center">
    <div className={`mb-4 ${!match ? 'max-w-lg' : ''}`}>
      {/* Description and Features - Only show when not in call */}
      {!match && (
        <>
          <h3 className="text-gray-900 font-medium mb-2">
            TalkConnect - Where Voices Find Connection
          </h3>
          <p className="text-gray-600 text-sm mb-4">
            Everyone has a story to share or a moment they wish someone understood. 
            Whether you need to talk or listen, TalkConnect pairs you with someone who&apos;s ready to be there.
            Choose your role, set your mood, and start a heartfelt conversation—no distractions, no judgment, just real connection.
          </p>
          
          {/* Features Pills */}
          <div className="flex flex-wrap justify-center gap-2 mb-6">
            <span className="bg-teal-50 text-teal-600 px-3 py-1 rounded-full text-sm font-medium">
              Safe Space
            </span>
            <span className="bg-teal-50 text-teal-600 px-3 py-1 rounded-full text-sm font-medium">
              Real Conversations
            </span>
            <span className="bg-teal-50 text-teal-600 px-3 py-1 rounded-full text-sm font-medium">
              Anonymous
            </span>
          </div>
        </>
      )}

      {/* Support Button - Always show but with different margin when in call */}
      <div className="flex flex-col items-center pb-20"> {/* Added pb-20 for space above fixed button */}
        <a
          href="https://www.buymeacoffee.com/talkconnect"
          target="_blank"
          rel="noopener noreferrer"
          className={`inline-flex items-center px-4 py-2 rounded-lg bg-yellow-100 hover:bg-yellow-200 
            transition-colors text-yellow-800 font-medium ${match ? 'mt-4' : ''}`}
        >
          <Coffee className="w-5 h-5 mr-2 text-yellow-700" />
          Support TalkConnect
        </a>

        {/* Social Media Bar - Now inside the padded container */}
        <div className="flex justify-center items-center gap-6 mt-4">
          {/* Facebook */}
          <a
            href="https://facebook.com/talkconnect"
            target="_blank"
            rel="noopener noreferrer"
            className="group"
            aria-label="Facebook"
          >
            <svg
              className="w-5 h-5 text-gray-400 group-hover:text-[#1877F2] transition-colors"
              fill="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"
                clipRule="evenodd"
              />
            </svg>
          </a>

          {/* Instagram */}
          <a
            href="https://instagram.com/talkconnect"
            target="_blank"
            rel="noopener noreferrer"
            className="group"
            aria-label="Instagram"
          >
            <svg
              className="w-5 h-5 text-gray-400 group-hover:text-[#E4405F] transition-colors"
              fill="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z"
                clipRule="evenodd"
              />
            </svg>
          </a>

          {/* Twitter */}
          <a
            href="https://twitter.com/talkconnect"
            target="_blank"
            rel="noopener noreferrer"
            className="group"
            aria-label="Twitter"
          >
            <svg
              className="w-5 h-5 text-gray-400 group-hover:text-[#1DA1F2] transition-colors"
              fill="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
            </svg>
          </a>

          {/* Reddit - Fixed with correct Snoo logo */}
        <a
        href="https://reddit.com/r/talkconnect"
        target="_blank"
        rel="noopener noreferrer"
        className="group"
        aria-label="Reddit"
        >
        <svg 
              className="w-5 h-5 text-gray-400 group-hover:text-[#FF4500] transition-colors"
              fill="currentColor"
              viewBox="0 0 24 24" 
              aria-hidden="true"
        >
              <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/>
           </svg>
          </a>
        </div>
      </div>
    </div>
  </div>
</div>

        {/* Error Display */}
        {error && (
  <div className="bg-rose-50 border border-rose-200 text-rose-600 px-4 py-3 rounded-lg mb-3">
    <div className="flex items-center">
      <AlertTriangle className="h-5 w-5 mr-2" />
      <span className="font-medium">
        {error || 'Unable to connect to server. Please try refreshing.'}
      </span>
    </div>
    <button
      onClick={() => setError(null)}
      className="mt-2 text-sm text-rose-600 hover:text-rose-700"
    >
      Dismiss
    </button>
  </div>
)}

{/* Bunny Helper */}
<BunnyHelper />

        {/* Call Controls - Fixed at bottom */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 z-40">
  <div className="max-w-4xl mx-auto px-4 py-3">
    <div className="flex justify-center space-x-3">
      {/* Mute Button */}
      {(callStatus === 'connected' || callStatus === 'peer_disconnected') && (
        <button
          onClick={handleMuteToggle}
          className={`flex-1 flex items-center justify-center px-4 py-2.5 rounded-lg font-medium 
            transition-colors ${isMuted 
              ? 'bg-white border-2 border-rose-500 text-rose-500 hover:bg-rose-50' 
              : 'bg-white border-2 border-teal-500 text-teal-500 hover:bg-teal-50'
            }`}
          type="button"
          aria-pressed={isMuted}
          disabled={!audioPermissionGranted}
        >
          <div className="flex items-center">
            {isMuted ? (
              <>
                <MicOff className="w-5 h-5 mr-2" />
                <span>Unmute</span>
              </>
            ) : (
              <>
                <Mic className="w-5 h-5 mr-2" />
                <span>Mute</span>
              </>
            )}
          </div>
        </button>
      )}

          {/* Find Match/End Call Button */}
          <button
        onClick={async () => {
          if (callStatus === 'connected' || callStatus === 'peer_disconnected') {
            await handleEndCall();
          } else {
            await handleFindMatch();
          }
        }}
        disabled={callStatus === 'finding' || isProcessingMatch || 
                  callStatus === 'disconnecting' || !audioPermissionGranted}
        className={`flex-1 flex items-center justify-center px-4 py-2.5 rounded-lg font-medium
          transition-colors ${
          (callStatus === 'finding' || isProcessingMatch || callStatus === 'disconnecting')
            ? 'bg-gray-300 cursor-not-allowed' 
            : (callStatus === 'connected' || callStatus === 'peer_disconnected')
              ? 'bg-rose-500 hover:bg-rose-600'
              : 'bg-teal-500 hover:bg-teal-600'
          } text-white`}
      >
        <Phone className="w-5 h-5 mr-2" />
        <span>
          {callStatus === 'connected' || callStatus === 'peer_disconnected' ? 'End Call' :
           callStatus === 'disconnecting' ? 'Ending...' :
           callStatus === 'finding' || isProcessingMatch ? 'Finding...' :
           'Find Match'}
        </span>
      </button>
    </div>
  </div>
</div>

       {/* Rating Dialog Modal */}
{showRating && (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
    <div className="bg-white rounded-xl w-full max-w-sm mx-4 p-6 animate-fadeIn">
      <h3 className="text-xl font-semibold text-gray-900 mb-2">
        Rate your Listener
      </h3>
      <p className="text-gray-600 text-sm mb-6">
        How was your conversation?
      </p>
      <div className="flex justify-center space-x-3 mb-8">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            onClick={() => setCurrentRating(star)}
            className="transform transition-transform hover:scale-110 focus:outline-none"
          >
            <Star
              className={`w-8 h-8 transition-colors ${
                star <= currentRating 
                  ? 'text-yellow-400 fill-current' 
                  : 'text-gray-300'
              }`}
            />
          </button>
        ))}
      </div>
      <div className="flex flex-col space-y-3">
        <button
          onClick={handleRatingSubmit}
          disabled={isSubmittingRating || !currentRating}
          className={`w-full py-3 px-4 rounded-lg font-medium transition-colors
            ${currentRating 
              ? 'bg-teal-500 hover:bg-teal-600 text-white' 
              : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}
        >
          {isSubmittingRating ? 'Submitting...' : 'Submit Rating'}
        </button>
        <button
          onClick={handleSkipRating}
          disabled={isSubmittingRating}
          className="w-full py-3 px-4 rounded-lg font-medium text-gray-600 
            hover:bg-gray-50 transition-colors border border-gray-200"
        >
          Skip Rating
        </button>
      </div>
    </div>
  </div>
)}
        {/* Sidebar/Profile Modal */}
{isSidebarOpen && (
  <div className="fixed inset-0 bg-black/50 z-50">
    <div 
      className="absolute right-0 top-0 h-full w-72 bg-white shadow-lg animate-slideIn"
      role="dialog"
      aria-modal="true"
      aria-label="Profile sidebar"
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-100 flex justify-between items-center">
        <h2 className="text-lg font-semibold text-gray-900">Profile</h2>
        <button
          onClick={() => setIsSidebarOpen(false)}
          className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
          aria-label="Close sidebar"
        >
          <XIcon className="w-4 h-4 text-gray-500" />
        </button>
      </div>
      
      {/* Content */}
      <div className="px-4 py-3 space-y-4">
        {/* Username */}
        <div>
          <span className="text-gray-600 text-sm">Username</span>
          <p className="text-gray-900 font-medium">{username}</p>
        </div>

        {/* Rating */}
        <div>
          <span className="text-gray-600 text-sm">Rating</span>
          <div className="flex items-center text-gray-900">
            <Star className="w-4 h-4 text-yellow-400 fill-current mr-1" />
            <span className="font-medium">{rating}</span>
          </div>
        </div>

        {/* Current Role */}
        <div>
          <span className="text-gray-600 text-sm">Current Role</span>
          <div className="flex items-center text-teal-600 bg-teal-50 
            px-2.5 py-1 rounded-full w-fit mt-1">
            {isTalker ? (
              <>
                <Mic className="w-3.5 h-3.5 mr-1.5" />
                <span className="font-medium">Talker</span>
              </>
            ) : (
              <>
                <Headphones className="w-3.5 h-3.5 mr-1.5" />
                <span className="font-medium">Listener</span>
              </>
            )}
          </div>
        </div>

        {/* Status */}
        <div>
          <span className="text-gray-600 text-sm">Status</span>
          <div className="flex items-center text-gray-900 bg-gray-50 
            px-2.5 py-1 rounded-full w-fit mt-1">
            <span className="font-medium">
              {isAvailableForMatch ? 'Available for Matching' : 'Not Available'}
            </span>
          </div>
        </div>
      </div>

      {/* Logout Button */}
      <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-100">
        <button
          onClick={handleLogout}
          className="w-full bg-rose-500 hover:bg-rose-600 text-white font-medium
            py-2.5 rounded-lg transition-colors"
        >
          Logout
        </button>
      </div>
    </div>
  </div>
)}

{/* Animation styles */}
<style jsx>{`
  @keyframes slideIn {
    from { transform: translateX(100%); }
    to { transform: translateX(0); }
  }
  .animate-slideIn {
    animation: slideIn 0.2s ease-out;
  }
`}</style>

        {/* Audio Permission Dialog */}
{!audioPermissionGranted && (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
    <div className="bg-white rounded-xl w-full max-w-sm mx-4 p-6">
      <h3 className="text-xl font-semibold text-gray-900 mb-2">
        Microphone Access Required
      </h3>
      <p className="text-gray-600 mb-6">
        TalkConnect needs access to your microphone for voice calls.
        Please allow microphone access when prompted.
      </p>
      <button
        onClick={checkAndRequestAudioPermission}
        className="w-full bg-teal-500 hover:bg-teal-600 text-white font-medium
          py-3 rounded-lg transition-colors"
      >
        Grant Access
      </button>
    </div>
  </div>
)}
      </div>
    </div>
    </TutorialProvider>
  );
}

export default Match;